import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { CalendarPlus } from "lucide-react";
import { toast } from "sonner";

export function NewPeriodDialog({ open, onOpenChange, existingPeriods = [], onCreated }) {
  // 既存の対象月から、未作成の月を初期値に
  const now = new Date();
  const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const [year, setYear] = useState(nextMonth.getFullYear());
  const [month, setMonth] = useState(nextMonth.getMonth() + 1);
  const [deadline, setDeadline] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // ダイアログが開かれたら、既存に無い直近の月をデフォルトにセット
  useEffect(() => {
    if (!open) return;
    const existing = new Set(existingPeriods.map((p) => `${p.year}-${p.month}`));
    let y = nextMonth.getFullYear();
    let m = nextMonth.getMonth() + 1;
    for (let i = 0; i < 24; i++) {
      if (!existing.has(`${y}-${m}`)) break;
      m++;
      if (m > 12) { m = 1; y++; }
    }
    setYear(y); setMonth(m);
    // デフォルト締め切り: 対象月の前月25日
    const dl = new Date(y, m - 1, 0);
    dl.setDate(25);
    setDeadline(dl.toISOString().slice(0, 10));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!year || !month) {
      toast.error("対象月を選択してください");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/shift-periods", {
        year: Number(year),
        month: Number(month),
        submission_deadline: deadline || null,
        status: "accepting",
      });
      toast.success(`${year}年${month}月の対象月を作成しました`);
      await onCreated?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  // 過去2ヶ月〜先12ヶ月の選択肢
  const yearOpts = [];
  for (let y = now.getFullYear() - 1; y <= now.getFullYear() + 2; y++) yearOpts.push(y);
  const monthOpts = Array.from({ length: 12 }, (_, i) => i + 1);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="new-period-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarPlus className="w-5 h-5 text-blue-600" />新しい対象月を作成
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-xs text-slate-500">
            シフトを作成する対象月を作ります。作成後、スタッフが希望提出できるようになります。
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold text-slate-700">年</Label>
              <select
                value={year} onChange={(e) => setYear(Number(e.target.value))}
                className="mt-1 w-full text-sm border border-slate-200 rounded-md p-2"
                data-testid="new-period-year"
              >
                {yearOpts.map((y) => <option key={y} value={y}>{y}年</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs font-bold text-slate-700">月</Label>
              <select
                value={month} onChange={(e) => setMonth(Number(e.target.value))}
                className="mt-1 w-full text-sm border border-slate-200 rounded-md p-2"
                data-testid="new-period-month"
              >
                {monthOpts.map((m) => <option key={m} value={m}>{m}月</option>)}
              </select>
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold text-slate-700">希望提出締切日 (任意)</Label>
            <input
              type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)}
              className="mt-1 w-full text-sm border border-slate-200 rounded-md p-2"
              data-testid="new-period-deadline"
            />
            <p className="text-[10px] text-slate-400 mt-1">スタッフの希望提出締切日。空欄でもOK。</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="new-period-cancel">キャンセル</Button>
          <Button onClick={submit} disabled={submitting} className="bg-blue-600 hover:bg-blue-700" data-testid="new-period-submit">
            {submitting ? "作成中…" : "作成する"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
